import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import JsonLd from 'components/generic/JsonLd'
import {
  breadcrumbList,
  isoDuration,
  ORGANIZATION,
  SITE_URL,
  youtubeEmbedUrl
} from 'components/utils/structuredData'
import { tutorialsStatus } from 'reduxModules/tutorials'
import { tutorialStateSelector as stateSelector } from 'reduxModules/tutorials/selectors'
import { tutorialDispatchers as dispatchers } from 'reduxModules/tutorials/dispatchers'
import AssetGalleryPropTypes from 'components/generic/AssetsGallery/propTypes'
import LoadingComponent from 'components/generic/LoadingComponent'
import { SubscriptionPlanPropTypes } from 'components/SubscriptionPlans/SubscriptionPlanList'
import { TutorialSummaryPropTypes } from 'components/generic/CourseSummary/propTypes'
import PLANS from 'components/SubscriptionPlans/fixturePlans'
import withRouterV6 from '../../../utils/withRouterV6'

import CourseTabs from '../../Courses/CourseTabs'
import AssetsGallery from '../../generic/AssetsGallery/_lazy'
import SectionTitle from '../../generic/SectionTitle'
import CourseArtist from '../../Courses/CourseArtist'
import CoursePurchase from '../../Courses/CoursePurchase/index.jsx'
import CourseRelated from '../../Courses/CourseRelated'
import Banner from '../../generic/FeaturedElement/index.jsx'
import VideoModal from '../../generic/VideoModal'
import TutorialPlayer from '../TutorialPlayer/index'
import CertificateBanner from './CertificateBanner'
import LeadMagnetFunnelForm from './LeadMagnetFunnelForm'
import SubscribeDialogCTA from '../../App/SubscribeDialogCTA'
import Overview from './Overview/_lazy'
import Curriculum from './NewCurriculum/index'
import Materials from './Materials/index.jsx'

import './styles.css'

export const TITLE_FORMAT = /^\s*(.*) -+ (\d\d:\d\d:\d\d)\s*$/

export class TutorialDetail extends Component {
  constructor(props) {
    super(props)
    const subscriptions = PLANS.filter(
      (p) => p.name !== 'Apprentice' && p.name !== 'Group Learning'
    ).map((plan, index) => ({
      name: plan.name,
      price: `$${plan.price}`,
      url: plan.url,
      icon: `icon-monster-${index + 1}`,
      details: [
        plan.description1,
        plan.checkoutDescription2,
        plan.description3
      ],
      best: true,
      plan
    }))
    this.state = {
      purchaseCategories: [
        {
          name: 'subscriptions',
          order: 3,
          cta: 'Subscribe',
          selector: subscriptions,
          default: true
        }
      ],
      purchaseCategoriesSidebar: [],
      playlist: [],
      watchMode: false,
      isFloating: false
    }
    this.getLinkTo = this.getLinkTo.bind(this)
  }

  componentDidMount() {
    const { loadPathInfo, query } = this.props

    const fetch = async () => {
      await this.props.loadTutorialInfo()
      if (query && query.path) await loadPathInfo()
      // /tutorials/<slug>/user/ is auth-only; skip it (and its 401) when logged out.
      if (this.props.isAuth) this.props.loadTutorialUserInfo()
    }

    fetch()
  }

  componentDidUpdate(prevProps) {
    const { params, query } = this.props
    const oldSlug = prevProps.params.tutorialSlug
    const newSlug = params.tutorialSlug

    // Handle slug change - clear and reload tutorial
    if (oldSlug !== newSlug) {
      this.props.clearTutorial()
      this.props.loadTutorialInfo()
      if (this.props.isAuth) this.props.loadTutorialUserInfo()
      return // Don't process other updates when slug changes
    }

    // Handle watch mode state changes
    const shouldBeWatchMode = query?.watch === '1'
    if (shouldBeWatchMode !== this.state.watchMode) {
      if (shouldBeWatchMode && !this.props.isAuth) {
        // User not authenticated, redirect away from watch mode
        this.props.router.push(this.props.location.pathname)
      } else {
        this.setState({ watchMode: shouldBeWatchMode })
      }
    }

    // Handle purchase categories updates
    this.updatePurchaseCategories(prevProps)
  }

  updatePurchaseCategories(prevProps) {
    const { dvdSets, miniLesson, url, price, regularPrice, owned, subscription } = this.props

    // Check if any relevant props changed
    const dvdSetsChanged = dvdSets !== prevProps.dvdSets
    const urlChanged = url !== prevProps.url
    const priceChanged = price !== prevProps.price
    const ownedChanged = owned !== prevProps.owned
    const subscriptionChanged = subscription !== prevProps.subscription

    if (!dvdSetsChanged && !urlChanged && !priceChanged && !ownedChanged && !subscriptionChanged) {
      return // No relevant changes
    }

    let purchaseCategories = [...this.state.purchaseCategories]
    let purchaseCategoriesSidebar = [...this.state.purchaseCategoriesSidebar]

    // Handle DVD sets
    if (dvdSets && !miniLesson && dvdSetsChanged) {
      const dvds = dvdSets.map((d) => ({
        name: d.dvdType || 'dvd',
        price: d.price || d.regularPrice,
        regularPrice: d.regularPrice,
        url: d.url,
        icon: 'icon-dvd',
        details: [
          'Subscribers save up to 30%.',
          'Ideal for offline learning.',
          'DVD plays worldwide.'
        ],
        default: true
      }))

      purchaseCategoriesSidebar = purchaseCategoriesSidebar.filter(
        (e) => e.name !== 'dvd'
      )

      dvds.forEach((d, index) => {
        purchaseCategoriesSidebar.push({
          // Set default in case backend sends null value
          name: d.name || 'dvd',
          order: index,
          cta: 'Add to Cart',
          selector: [d]
        })
      })
    }

    // Handle Stream option
    if (urlChanged || priceChanged || (price && !_.find(purchaseCategories, { name: 'Stream' }))) {
      const stream = {
        name: 'Stream',
        price: price || regularPrice,
        regularPrice,
        url,
        icon: 'icon-stream',
        details: [
          'Subscribers save up to 30%.',
          'Watch on your computer, phone or tablet.',
          'Lifetime access to course stream.'
        ]
      }

      purchaseCategoriesSidebar = purchaseCategoriesSidebar.filter(
        (e) => e.name !== 'stream'
      )
      purchaseCategoriesSidebar.push({
        name: 'stream',
        order: 2,
        cta: 'Add to Cart',
        selector: [stream]
      })
    }

    // If course is owned, remove stream option
    if (owned && ownedChanged)
      purchaseCategories = purchaseCategories
        .filter((c) => c.name !== 'stream')
        .map((c) => ({ ...c, default: true }))

    // If user is subscriber, remove subscription option
    if (!_.isNull(subscription) && subscriptionChanged)
      purchaseCategories = purchaseCategories
        .filter((c) => c.name !== 'subscriptions')
        .map((c, index) => ({ ...c, default: index === 0 }))

    this.setState({ purchaseCategories, purchaseCategoriesSidebar })
  }

  componentWillUnmount() {
    this.props.clearTutorial()
  }

  getBannerText() {
    const { owned, subscription, startedByUser } = this.props
    if (owned || !_.isNull(subscription)) {
      if (startedByUser) return 'Resume Course'
      return 'Watch Course'
    }
    return 'Subscribe to Watch'
  }

  getLinkTo() {
    const { id, owned, subscription, slug, confirmWatchTutorial, query } =
      this.props
    const initialWatchPath = `/tutorials/${slug}?watch=1`
    const hasSubscription = !_.isNull(subscription)
    const hasPathway = _.has(query, 'path')
    const watchPath = hasPathway
      ? `${initialWatchPath}&path=${query.path}&path_id=${query.path_id}`
      : initialWatchPath
    const monthlyVideosSeen =
      _.has(subscription, 'monthlyVideosSeen') &&
      !!subscription.monthlyVideosSeen
        ? subscription.monthlyVideosSeen
        : []
    const currentVideo = hasSubscription
      ? _.find(monthlyVideosSeen, { id })
      : null

    if (
      owned ||
      (hasSubscription && subscription.rank > 1) ||
      (hasSubscription && subscription.free) ||
      (!_.isEmpty(monthlyVideosSeen) && _.isObject(currentVideo)) ||
      this.props.hijacking
    )
      return watchPath

    if (hasSubscription && subscription.rank === 1) {
      const limit = subscription.plan === 'Basic' ? 4 : 6
      const videosSeen = monthlyVideosSeen ? monthlyVideosSeen.length : 0
      return () => {
        confirmWatchTutorial(watchPath, videosSeen, limit)
      }
    }

    return ''
  }

  render() {
    if (this.props.requestStatus !== tutorialsStatus.success)
      return <LoadingComponent />

    const { props } = this
    const subscribed = !_.isNull(props.subscription)
    const metaTitle = props.metaTitle || props.title
    const metaDescription = props.metaDescription || props.description
    const trailerEmbedUrl = youtubeEmbedUrl(props.trailer?.web)
    const cleanCategories = _.uniqBy(this.state.purchaseCategoriesSidebar, 'name')

    return (
      <article id="TutorialDetail">
        <Helmet>
          <title>{metaTitle} | Stan Winston School of Character Arts</title>
          <meta name="description" content={metaDescription} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          {!props.facebookShareImages.length && _.has(props, 'photo.original') && (
            <meta property="og:image" content={props.photo.original} />
          )}
          {props.facebookShareImages.length > 0 && props.facebookShareImages.map((fbImage, fbImageIndex) => (
            <meta property="og:image" content={fbImage.original} key={fbImageIndex} />
          ))}
          <meta property="og:type" content="article" />
        </Helmet>

        {props.photo && (
          <div className="header">
            {!this.state.watchMode && (
              <div className="wrapper tutorial-detail-wrapper">
                <Banner
                  title={props.title}
                  linkTo={this.getLinkTo()}
                  btn={{ text: this.getBannerText(), action: this.getLinkTo() }}
                  description={props.description}
                  photo={props.photo}
                  onClick={props.videoPreviewInfo && props.tutorialTrailerStatus === tutorialsStatus.tutorialTrailerSuccess ? () => props.toggleVideoPreview() : null}
                  isMobile={props.isMobile}
                  usedFreeTrial={props.usedFreeTrial}
                  artist={`${props.artist.firstName} ${props.artist.lastName}`}
                  new={props.new}
                />
              </div>
            )}
            {this.state.watchMode && this.props.id && (
              <div>
                <TutorialPlayer
                  location={props.location}
                  artist={props.artist}
                  isAuth={props.isAuth}
                  slug={props.slug}
                  router={props.router}
                />
              </div>
            )}
          </div>
        )}

        {props.slug && (
          <CertificateBanner slug={props.slug} isAuth={props.isAuth} />
        )}

        <section className="wrapper overview">
          <Overview
            {...props}
            categories={this.state.purchaseCategoriesSidebar}
          />
        </section>

        {this.props.chaptered && (
          <section className="wrapper curriculum">
            <SectionTitle>Curriculum</SectionTitle>
            <Curriculum {...props} />
          </section>
        )}

        <section className="wrapper materials">
          <SectionTitle>Materials</SectionTitle>
          <Materials {...props} />
        </section>

        <div className="gallery-holder">
          {props.assets && props.assets.length > 0 && (
            <AssetsGallery
              assets={props.assets}
              isMobile={props.isMobile}
              showThumbnails
            />
          )}
        </div>

        {!_.isEmpty(props.artist) && <CourseArtist {...props.artist} />}

        {!props.isSubscribed && (
          <section className="subscribe-cta-banner">
            {!props.usedFreeTrial && (
              <p className="subscribe-cta-banner__content">Free 7-day Trial</p>
            )}
            <SubscribeDialogCTA>
              <div className="subscribe-cta-banner__cta btn -wide -orange">
                <span>Subscribe to Watch</span>
              </div>
            </SubscribeDialogCTA>
          </section>
        )}

        <section className="wrapper additional-purchase-options">
          <SectionTitle>Additional Purchasing Options</SectionTitle>
          {this.state.purchaseCategoriesSidebar && (
            <CoursePurchase
              categories={cleanCategories}
              selectPlan={props.selectPlan}
              isMobile={props.isMobile}
              isSubscribed={props.isSubscribed}
              isOwned={props.owned}
              onSide
              isTutorial
              isSimpleSection
            />
          )}
        </section>

        {props.relatedTutorials.length > 0 && (
          <div className="wrapper">
            {props.relatedTutorials && props.tags && (
              <CourseRelated
                courses={props.relatedTutorials}
                tags={props.tags}
                isSubscribed={props.isSubscribed}
                isMobile={props.isMobile}
              />
            )}
          </div>
        )}

        {!props.isAuth && <LeadMagnetFunnelForm tag="Course Email Lead" surface="course" />}

        {props.videoPreviewInfo && (
          <VideoModal
            url={props.videoPreviewInfo.path}
            visible={props.videoPreview}
            close={() => props.toggleVideoPreview()}
            isYoutube={props.videoPreviewInfo.isYoutube}
            subtitles={props.videoPreviewInfo.subtitles}
          />
        )}

        {props.metaTitle && props.metaDescription && (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'Course',
              name: props.metaTitle,
              description: props.metaDescription,
              provider: ORGANIZATION,
              ...(props.photo?.original && { image: props.photo.original }),
              url: `${SITE_URL}/tutorials/${props.slug}`,
              ...((props.regularPrice || props.price) && {
                offers: {
                  '@type': 'Offer',
                  category: 'Paid',
                  price: props.regularPrice || props.price,
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/OnlineOnly',
                  url: `${SITE_URL}/tutorials/${props.slug}`
                }
              }),
              hasCourseInstance: {
                '@type': 'CourseInstance',
                courseMode: 'online',
                ...(isoDuration(props.durationH, props.durationM) && {
                  courseWorkload: isoDuration(props.durationH, props.durationM)
                }),
                ...(props.artist?.firstName && {
                  instructor: {
                    '@type': 'Person',
                    name: `${props.artist.firstName} ${
                      props.artist.lastName || ''
                    }`.trim()
                  }
                })
              }
            }}
          />
        )}
        {/* Google requires contentUrl or embedUrl on VideoObject; without a
            fetchable video URL the markup gets flagged in Search Console, so
            skip the block entirely when there is no YouTube trailer */}
        {props.s3TrailerThumbnail?.default && props.metaTitle && trailerEmbedUrl && (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: props.metaTitle,
              description: props.metaDescription || props.description,
              thumbnailUrl: props.s3TrailerThumbnail.default,
              embedUrl: trailerEmbedUrl,
              ...(props.created && { uploadDate: props.created }),
              ...(isoDuration(props.durationH, props.durationM) && {
                duration: isoDuration(props.durationH, props.durationM)
              })
            }}
          />
        )}
        {props.slug && (
          <JsonLd
            data={breadcrumbList([
              { name: 'Home', path: '/' },
              { name: 'Courses', path: '/tutorials' },
              {
                name: props.metaTitle || props.title,
                path: `/tutorials/${props.slug}`
              }
            ])}
          />
        )}
      </article>
    )
  }
}

export const TutorialPropTypes = {
  id: PropTypes.number,
  artist: PropTypes.shape({
    title: PropTypes.string,
    intro: PropTypes.string,
    photoThumbnail: PropTypes.objectOf(PropTypes.string).isRequired
  }),
  photo: PropTypes.objectOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  slug: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  materials: PropTypes.string,
  referenceMaterials: PropTypes.string,
  trailer: PropTypes.shape({
    web: PropTypes.string,
    raw: PropTypes.shape({
      low: PropTypes.string,
      high: PropTypes.string
    })
  }),
  tags: PropTypes.arrayOf(PropTypes.string),
  price: PropTypes.number,
  regularPrice: PropTypes.number,
  url: PropTypes.string,
  dvdSet: PropTypes.shape({
    url: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    regularPrice: PropTypes.number
  }),
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      order: PropTypes.number,
      subtitles: PropTypes.arrayOf(PropTypes.object),
      firstFrameImage: PropTypes.objectOf(PropTypes.string)
    })
  ),
  owned: PropTypes.bool,
  selectPlan: PropTypes.func,
  startedByUser: PropTypes.bool
}

export const TutorialDefaultProps = {
  id: 0,
  materials: '',
  referenceMaterials: '',
  description: '',
  url: '',
  price: 0,
  regularPrice: 0,
  dvdSet: {
    url: '',
    price: 0
  },
  owned: false,
  selectPlan: null
}

TutorialDetail.propTypes = {
  ...TutorialPropTypes,
  assets: PropTypes.arrayOf(PropTypes.shape(AssetGalleryPropTypes)).isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape(SubscriptionPlanPropTypes))
    .isRequired,
  related: PropTypes.arrayOf(PropTypes.shape(TutorialSummaryPropTypes))
    .isRequired,
  subscription: PropTypes.shape({
    monthlyVideosSeen: PropTypes.arrayOf(
      PropTypes.shape(TutorialSummaryPropTypes)
    ),
    plan: PropTypes.string.isRequired,
    rank: PropTypes.number.isRequired
  }),
  loadSubscriptionPlans: PropTypes.func.isRequired,
  loadTutorialInfo: PropTypes.func.isRequired,
  loadTutorial: PropTypes.func,
  location: PropTypes.shape({
    hash: PropTypes.string,
    pathname: PropTypes.string
  }).isRequired
}

TutorialDetail.defaultProps = {
  ...TutorialDefaultProps,
  assets: [],
  plans: [],
  related: []
}

export default withRouterV6(connect(stateSelector, dispatchers)(TutorialDetail))
